import type {
  ComponentPropsWithRef,
  ElementRef,
  HTMLAttributes,
  ReactNode,
} from 'react'
import { forwardRef } from 'react'
import type {
  InputProps as AriaInputProps,
  TextAreaProps as AriaTextAreaProps,
} from 'react-aria-components'
import {
  Input as AriaInput,
  TextArea as AriaTextArea,
  composeRenderProps,
} from 'react-aria-components'
import type { VariantProps } from 'tailwind-variants'
import { tv } from 'tailwind-variants'
import { cx } from 'src/utils/cx'

export interface UITextFieldWithIconProps extends ComponentPropsWithRef<'input'> {
  /**
   * The style variant. `undefined` for un-styled button.
   */
  variant?: 'normal'
  /**
   * The icon prepended/appended to the button label.
   * Syntax: `[ startIcon, endIcon ]`
   * `""` to render no icon but keep the space (mostly for centering the label).
   */
  icon?: ReactNode | [ReactNode, ReactNode]
  wrapperProps?: HTMLAttributes<HTMLDivElement>
}

const classes = {} as Record<
  Exclude<UITextFieldWithIconProps['variant'], undefined>,
  string
>

const UITextFieldWithIcon = forwardRef<
  ElementRef<'input'>,
  UITextFieldWithIconProps
>((props, ref) => {
  const { variant, icon, wrapperProps, ...inputProps } = props

  const [startIcon, endIcon] = Array.isArray(icon) ? icon : [icon]

  return (
    <div
      {...wrapperProps}
      className={cx('grid', wrapperProps?.className)}
      style={{
        gridTemplateColumns: [
          startIcon != null ? '32px' : '',
          'minmax(0,1fr)',
          endIcon != null ? '32px' : '',
        ].join(' '),
        ...wrapperProps?.style,
      }}
    >
      {startIcon && <span>{startIcon}</span>}
      <input
        ref={ref}
        {...inputProps}
        disabled={props.disabled}
        className={cx(
          'relative h-full outline-none',
          ...(variant
            ? [classes[variant], props.children && 'min-w-[5rem] px-3']
            : []),
          inputProps?.className,
        )}
      />
      {endIcon && <span>{endIcon}</span>}
    </div>
  )
})
UITextFieldWithIcon.displayName = 'UITextFieldWithIcon'

export default UITextFieldWithIcon

type UIInputProps = AriaInputProps &
  VariantProps<typeof inputStyles> &
  StyleProps & {
    isError?: boolean
  }
const inputStyles = tv({
  base: 'border',
  variants: {
    variant: {
      'rounded-normal':
        'bg-white dark:bg-zinc-900 text-gray-800 dark:text-zinc-200 border-gray-200 dark:border-zinc-700 outline-none ring-0 focus:ring-1 focus:ring-zinc-700 focus:dark:ring-zinc-400 rounded-lg py-2 px-4 placeholder:text-neutral-400 placeholder:dark:text-neutral-500',
    },
    isError: {
      true: 'border-red-500 dark:border-red-400 focus:ring-red-500 focus:dark:ring-red-400',
    },
  },
  compoundVariants: [
    {
      variant: 'rounded-normal',
      isDisabled: true,
      className: 'cursor-not-allowed text-neutral-400',
    },
    {
      variant: 'rounded-normal',
      isError: true,
      className:
        'bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100 animate-shake',
    },
  ],
})

export function UIInput(props: UIInputProps) {
  const { variant, className, isError, ...rest } = props

  return (
    <AriaInput
      {...rest}
      className={composeRenderProps(className, (className, renderProps) =>
        inputStyles({ ...renderProps, variant, isError, className }),
      )}
    />
  )
}

type UITextAreaProps = AriaTextAreaProps &
  VariantProps<typeof textAreaStyles> &
  StyleProps

const textAreaStyles = tv({
  variants: {
    variant: {
      'rounded-normal':
        'border bg-white dark:bg-zinc-900 text-gray-800 dark:text-zinc-200 border-gray-200 dark:border-zinc-700 outline-none ring-0 focus:ring-1 focus:ring-zinc-700 focus:dark:ring-zinc-400 rounded-lg py-2 px-4 placeholder:text-neutral-400 placeholder:dark:text-neutral-500',
    },
  },
})

export function UITextArea(props: UITextAreaProps) {
  const { variant, className, ...rest } = props

  return (
    <AriaTextArea
      {...rest}
      className={composeRenderProps(className, (className, renderProps) =>
        textAreaStyles({ ...renderProps, variant, className }),
      )}
    />
  )
}
